import { Input, Select, SelectItem, Textarea } from "@nextui-org/react";
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { Controller, Control, FieldErrors, UseFormSetValue, useFieldArray } from 'react-hook-form';
import { Icon } from "@iconify/react";
import { CtyButton, CopyCodeField } from "@circuitry-ai/doc-ui";

export interface CredentialFormDetails {
    name: string;
    description: string;
    credential_type: string;
    
    // Config fields
    token_url?: string;
    grant_type?: string;
    scope?: string;
    token_ttl_seconds?: number;
    access_cookie_name?: string;
    header_name?: string;
    prefix?: string;
    http_method?: string;
    request_body_template?: string;
    response_token_path?: string;
    response_expiry_path?: string;
    headers?: string;
    key?: string;
    value_type?: string;
    
    // Secrets fields
    client_id?: string;
    client_secret?: string;
    username?: string;
    password?: string;
    api_key?: string;
    api_secret?: string;
    token?: string;
    value?: string;
    value_object?: string;
    key_value_pairs?: Array<{ key: string; value: string }>;
    
    // Custom secrets (key-value pairs)
    custom_secrets?: Array<{ key: string; value: string }>;

    tags?: string[];
}

interface CredentialFormProps {
    control: Control<CredentialFormDetails>;
    errors: FieldErrors<CredentialFormDetails>;
    watch: any;
    setValue: UseFormSetValue<CredentialFormDetails>;
    code?: string | null;
    isCreateMode?: boolean;
    onCodeChange?: (value: string) => void;
}

interface FormFieldConfig {
    name: keyof CredentialFormDetails;
    label: string;
    placeholder: string;
    required?: boolean;
    type?: 'text' | 'number' | 'password';
    colSpan?: 'full' | 'half';
    optional?: boolean;
}

interface FormSelectConfig {
    name: keyof CredentialFormDetails;
    label: string;
    placeholder?: string;
    required?: boolean;
    options: Array<{ key: string; value: string }>;
    defaultValue?: string;
    colSpan?: 'full' | 'half';
}

interface FormTextareaConfig {
    name: keyof CredentialFormDetails;
    label: string;
    placeholder: string;
    required?: boolean;
    minRows?: number;
    maxRows?: number;
    colSpan?: 'full' | 'half';
    validateJson?: boolean;
}

interface SectionConfig {
    title: string;
    icon: string;
    fields: Array<FormFieldConfig | FormSelectConfig | FormTextareaConfig>;
}

// Reusable Form Field Component
const FormField: React.FC<{
    name: keyof CredentialFormDetails;
    control: Control<CredentialFormDetails>;
    errors: FieldErrors<CredentialFormDetails>;
    label: string;
    placeholder: string;
    required?: boolean;
    type?: 'text' | 'number' | 'password';
    colSpan?: 'full' | 'half';
    optional?: boolean;
}> = ({ name, control, errors, label, placeholder, required, type = 'text', colSpan = 'full', optional }) => {
    const { t } = useTranslation();
    const colClass = colSpan === 'full' ? 'md:col-span-2' : '';
    
    return (
        <Controller
            name={name}
            control={control}
            rules={
                required
                    ? { required: t(`${label} is required`) }
                    : type === 'number'
                    ? { min: { value: 0, message: t(`${label} must be a positive number`) } }
                    : {}
            }
            render={({ field, fieldState, formState }) => (
                <div className={colClass}>
                    <label className="flex items-center text-sm pb-1 font-medium">
                        {t(label)}
                        {required && <span className="text-danger">*</span>}
                        {optional && <span className="text-default-500 text-xs ml-1">({t('optional')})</span>}
                    </label>
                    {type === 'number' ? (
                        <Input
                            {...field}
                            type="number"
                            size="sm"
                            placeholder={t(placeholder)}
                            value={field.value ? String(field.value) : ''}
                            onValueChange={(value) => field.onChange(value ? Number(value) : undefined)}
                        />
                    ) : (
                        <Input {...field} size="sm" type={type} placeholder={t(placeholder)} value={field.value ? String(field.value) : ''} />
                    )}
                    {fieldState.error && (fieldState.isTouched || formState.isSubmitted) && (
                        <p className="text-danger text-xs mt-1">{fieldState.error.message}</p>
                    )}
                </div>
            )}
        />
    );
};

// Reusable Form Select Component
const FormSelect: React.FC<{
    name: keyof CredentialFormDetails;
    control: Control<CredentialFormDetails>;
    errors: FieldErrors<CredentialFormDetails>;
    label: string;
    placeholder?: string;
    required?: boolean;
    options: Array<{ key: string; value: string }>;
    defaultValue?: string;
    colSpan?: 'full' | 'half';
}> = ({ name, control, errors, label, placeholder, required, options, defaultValue, colSpan = 'full' }) => {
    const { t } = useTranslation();
    const colClass = colSpan === 'full' ? 'md:col-span-2' : '';
    
    return (
        <Controller
            name={name}
            control={control}
            rules={required ? { required: t(`${label} is required`) } : {}}
            render={({ field, fieldState, formState }) => (
                <div className={colClass}>
                    <label className="flex items-center text-sm pb-1 font-medium">
                        {t(label)}
                        {required && <span className="text-danger">*</span>}
                    </label>
                    <Select
                        id={name}
                        aria-label={name}
                        disallowEmptySelection
                        selectedKeys={field.value ? new Set([String(field.value)]) : (defaultValue ? new Set([defaultValue]) : new Set())}
                        size="sm"
                        placeholder={placeholder ? t(placeholder) : undefined}
                        onSelectionChange={(keys) => {
                            const selectedValue = Array.from(keys)[0] as string;
                            field.onChange(selectedValue);
                        }}
                    >
                        {options.map((option) => (
                            <SelectItem key={option.key} value={option.key}>
                                {t(option.value)}
                            </SelectItem>
                        ))}
                    </Select>
                    {fieldState.error && (fieldState.isTouched || formState.isSubmitted) && (
                        <p className="text-danger text-xs mt-1">{fieldState.error.message}</p>
                    )}
                </div>
            )}
        />
    );
};

// Reusable Form Textarea Component
const FormTextarea: React.FC<{
    name: keyof CredentialFormDetails;
    control: Control<CredentialFormDetails>;
    errors: FieldErrors<CredentialFormDetails>;
    label: string;
    placeholder: string;
    required?: boolean;
    minRows?: number;
    maxRows?: number;
    colSpan?: 'full' | 'half';
    validateJson?: boolean;
}> = ({ name, control, errors, label, placeholder, required, minRows = 3, maxRows = 6, colSpan = 'full', validateJson }) => {
    const { t } = useTranslation();
    const colClass = colSpan === 'full' ? 'md:col-span-2' : '';
    
    return (
        <Controller
            name={name}
            control={control}
            rules={{
                ...(required ? { required: t(`${label} is required`) } : {}),
                ...(validateJson
                    ? {
                        validate: (value: string | undefined) => {
                            if (!value?.trim()) {
                                return required ? t(`${label} is required`) : true;
                            }
                            try {
                                JSON.parse(value);
                                return true;
                            } catch {
                                return t('Invalid JSON format');
                            }
                        },
                    }
                    : {}),
            }}
            render={({ field, fieldState, formState }) => (
                <div className={colClass}>
                    <label className="flex items-center text-sm pb-1 font-medium">
                        {t(label)}
                        {required && <span className="text-danger">*</span>}
                    </label>
                    <Textarea
                        {...field}
                        size="sm"
                        minRows={minRows}
                        maxRows={maxRows}
                        placeholder={t(placeholder)}
                        value={field.value ? String(field.value) : ''}
                        classNames={{ input: "resize-y font-mono text-sm" }}
                    />
                    {fieldState.error && (fieldState.isTouched || formState.isSubmitted) && (
                        <p className="text-danger text-xs mt-1">{fieldState.error.message}</p>
                    )}
                </div>
            )}
        />
    );
};

// Reusable Section Header Component
const FormSection: React.FC<{ title: string; icon: string }> = ({ title, icon }) => {
    const { t } = useTranslation();
    return (
        <div className="flex items-center gap-2 mb-3">
            <Icon icon={icon} width="16" height="16" color="primary"/>
            <label className="text-sm font-semibold">{t(title)}</label>
        </div>
    );
};

export const CredentialForm: React.FC<CredentialFormProps> = ({ control, errors, watch, setValue, code, isCreateMode, onCodeChange }) => {
    const { t } = useTranslation();
    
    // Watch credential_type to show/hide configuration section
    const credentialType = watch('credential_type') || '';
    const grantType = watch('grant_type') || '';
    const headerName = watch('header_name') || '';
    const valueType = watch('value_type') || '';
    const { fields: customSecretFields, append: appendCustomSecret, remove: removeCustomSecret, replace: replaceCustomSecret } = useFieldArray({
        control,
        name: 'custom_secrets'
    });
    const { fields: keyValueFields, append: appendKeyValue, remove: removeKeyValue, replace: replaceKeyValue } = useFieldArray({
        control,
        name: 'key_value_pairs'
    });

    useEffect(() => {
        if (credentialType === 'custom' && customSecretFields.length === 0) {
            appendCustomSecret({ key: '', value: '' });
        }
    }, [customSecretFields.length, appendCustomSecret]);

    useEffect(() => {
        if (credentialType === 'key_value' && valueType === 'key_value' && keyValueFields.length === 0) {
            appendKeyValue({ key: '', value: '' });
        }
        if (credentialType !== 'key_value') {
            setValue('value', '');
            setValue('value_object', '');
            replaceKeyValue([]);
        }
    }, [credentialType, valueType, keyValueFields.length, appendKeyValue, replaceKeyValue, setValue]);

    const grantTypeOptions = useMemo(() => [
        { key: 'client_credentials', value: 'Client Credentials' },
        { key: 'password', value: 'Password' }
    ], []);

    const credentialTypeOptions = useMemo(() => [
        { key: 'oauth2', value: 'OAuth2 (Client Credentials / Password Grant)' },
        { key: 'api_key', value: 'API Key' },
        { key: 'basic_auth', value: 'Basic Auth' },
        { key: 'custom', value: 'Custom' },
        { key: 'bearer_static', value: 'Bearer Static' },
        { key: 'key_value', value: 'Key Value' },
        { key: 'cookie_password', value: 'Cookie (Password Login)'}
    ], []);
    
    const headerNameOptions = useMemo(() => [
        { key: 'Authorization', value: 'Authorization' },
        { key: 'X-API-Key', value: 'X-API-Key' },
        { key: 'api-key', value: 'api-key' },
        { key: 'custom', value: 'Custom' }
    ], []);

    const prefixOptions = useMemo(() => [
        { key: 'Bearer', value: 'Bearer' },
        { key: 'ApiKey', value: 'ApiKey' },
        { key: 'Token', value: 'Token' }
    ], []);

    const httpMethodOptions = useMemo(() => [
        { key: 'GET', value: 'GET' },
        { key: 'POST', value: 'POST' }
    ], []);
    const valueTypeOptions = useMemo(() => [
        { key: 'string', value: 'String' },
        { key: 'key_value', value: 'Key Value' },
        { key: 'object', value: 'Object' }
    ], []);

    const displayCredentialType = credentialType === 'oauth2'
        ? (grantType === 'password' ? 'oauth2_password' : 'oauth2_client_credentials')
        : credentialType;

    // Configuration for each credential type
    const credentialConfigs = useMemo(() => {
        const configs: Record<string, { config?: SectionConfig; secrets?: SectionConfig }> = {
            oauth2_client_credentials: {
                config: {
                    title: t('Configuration'),
                    icon: 'hugeicons:settings-02',
                    fields: [
                        { name: 'token_url', label: t('Token URL'), placeholder: 'Enter token URL', required: true, colSpan: 'full' },
                        { name: 'scope', label: t('Scope'), placeholder: 'Enter scope', optional: true, colSpan: 'full' },
                        { name: 'token_ttl_seconds', label: t('Token TTL (seconds)'), placeholder: 'Enter token TTL in seconds', type: 'number', colSpan: 'full' }
                    ]
                },
                secrets: {
                    title: t('Secrets'),
                    icon: 'hugeicons:lock',
                    fields: [
                        { name: 'client_id', label: t('Client ID'), placeholder: 'Enter client ID', required: true, colSpan: 'full' },
                        { name: 'client_secret', label: t('Client Secret'), placeholder: 'Enter client secret', required: true, colSpan: 'full' }
                    ]
                }
            },
            oauth2_password: {
                config: {
                    title: t('Configuration'),
                    icon: 'hugeicons:settings-02',
                    fields: [
                        { name: 'token_url', label: t('Token URL'), placeholder: 'Enter token URL', required: true, colSpan: 'full' },
                        { name: 'token_ttl_seconds', label: t('Token TTL (seconds)'), placeholder: 'Enter token TTL in seconds', type: 'number', colSpan: 'full' }
                    ]
                },
                secrets: {
                    title: t('Secrets'),
                    icon: 'hugeicons:lock',
                    fields: [
                        { name: 'client_id', label: t('Client ID'), placeholder: 'Enter client ID', required: true, colSpan: 'full' },
                        { name: 'client_secret', label: t('Client Secret'), placeholder: 'Enter client secret', required: true, colSpan: 'full' },
                        { name: 'username', label: t('Username'), placeholder: 'Enter username', required: true, colSpan: 'full' },
                        { name: 'password', label: t('Password'), placeholder: 'Enter password', required: true, type: 'password', colSpan: 'full' }
                    ]
                }
            },
            api_key: {
                config: {
                    title: t('Configuration'),
                    icon: 'hugeicons:settings-02',
                    fields: [
                        { name: 'header_name', label: t('Header Name'), placeholder: 'Select header name', required: true, options: headerNameOptions, colSpan: 'half' } as FormSelectConfig,
                        { name: 'prefix', label: t('Prefix'), placeholder: 'Select prefix', options: prefixOptions, colSpan: 'half' } as FormSelectConfig
                    ]
                },
                secrets: {
                    title: t('Secrets'),
                    icon: 'hugeicons:lock',
                    fields: [
                        { name: 'api_key', label: t('API Key'), placeholder: 'Enter API key', required: true, colSpan: 'full' }
                    ]
                }
            },
            basic_auth: {
                config: {
                    title: t('Configuration'),
                    icon: 'hugeicons:settings-02',
                    fields: [
                        { name: 'token_url', label: t('Token URL'), placeholder: 'Enter token URL', required: true, colSpan: 'full' },
                        {
                            name: 'headers',
                            label: t('Headers'),
                            placeholder: 'Enter headers (JSON format)',
                            required: true,
                            minRows: 4,
                            maxRows: 8,
                            colSpan: 'full',
                            validateJson: true,
                        } as FormTextareaConfig,
                        {
                            name: 'request_body_template',
                            label: t('Payload / Body'),
                            placeholder: 'Enter payload (JSON format)',
                            required: true,
                            minRows: 3,
                            maxRows: 6,
                            colSpan: 'full',
                            validateJson: true,
                        } as FormTextareaConfig,
                        {
                            name: 'token_ttl_seconds',
                            label: t('Token TTL (seconds)'),
                            placeholder: 'Enter token TTL in seconds',
                            type: 'number',
                            optional: true,
                            colSpan: 'full',
                        },
                    ]
                },
                secrets: {
                    title: t('Secrets'),
                    icon: 'hugeicons:lock',
                    fields: [
                        { name: 'username', label: t('Username'), placeholder: 'Enter username', required: true, colSpan: 'full' },
                        { name: 'password', label: t('Password'), placeholder: 'Enter password', required: true, type: 'password', colSpan: 'full' }
                    ]
                }
            },
            custom: {
                config: {
                    title: t('Configuration'),
                    icon: 'hugeicons:settings-02',
                    fields: [
                        { name: 'token_url', label: t('Token URL'), placeholder: 'Enter token URL', required: true, colSpan: 'full' },
                        { name: 'http_method', label: t('HTTP Method'), placeholder: 'Select HTTP method', required: true, options: httpMethodOptions, colSpan: 'half' } as FormSelectConfig,
                        { name: 'token_ttl_seconds', label: t('Token TTL (seconds)'), placeholder: 'Enter token TTL in seconds', type: 'number', colSpan: 'half' },
                        { name: 'request_body_template', label: t('Request Body Template'), placeholder: 'Enter request body template', minRows: 3, maxRows: 6, colSpan: 'full' } as FormTextareaConfig,
                        { name: 'response_token_path', label: t('Response Token Path'), placeholder: 'Enter response token path', colSpan: 'half' },
                        { name: 'response_expiry_path', label: t('Response Expiry Path'), placeholder: 'Enter response expiry path', colSpan: 'half' },
                        { name: 'headers', label: t('Headers'), placeholder: 'Enter headers (JSON format)', minRows: 2, maxRows: 4, colSpan: 'full' } as FormTextareaConfig
                    ]
                }
            },
            bearer_static: {
                secrets: {
                    title: t('Secrets'),
                    icon: 'hugeicons:lock',
                    fields: [
                        { name: 'token', label: t('Token'), placeholder: 'Enter token', required: true, colSpan: 'full' }
                    ]
                }
            },
            key_value: {
                config: {
                    title: t('Configuration'),
                    icon: 'hugeicons:settings-02',
                    fields: [
                        { name: 'key', label: t('Key'), placeholder: 'Enter key', required: true, colSpan: 'full' },
                        { name: 'value_type', label: t('Value Type'), placeholder: 'Select value type', required: true, options: valueTypeOptions, colSpan: 'full' } as FormSelectConfig
                    ]
                },
                secrets: {
                    title: t('Secrets'),
                    icon: 'hugeicons:lock',
                    fields: []
                }
            },
            cookie_password: {
                config: {
                    title: t('Configuration'),
                    icon: 'hugeicons:settings-02',
                    fields: [
                        { name: 'token_url', label: t('Token URL'), placeholder: t('Enter token URL'), required: true, colSpan: 'full' },
                        { name: 'access_cookie_name', label: t('Access Cookie Name'), placeholder: t('Enter access cookie name'), required: true, colSpan: 'full' },
                        { name: 'token_ttl_seconds', label: t('Token TTL (seconds)'), placeholder: t('Enter token TTL in seconds'), type: 'number', colSpan: 'full' }
                    ]
                },
                secrets: {
                    title: t('Secrets'),
                    icon: 'hugeicons:lock',
                    fields: [
                        { name: 'username', label: t('Username'), placeholder: t('Enter username'), required: true, colSpan: 'full' },
                        { name: 'password', label: t('Password'), placeholder: t('Enter password'), required: true, type: 'password', colSpan: 'full' }
                    ]
                }
            }
        };
        return configs;
    }, [headerNameOptions, prefixOptions, httpMethodOptions, valueTypeOptions]);

    const setGrantType = (grantType: string) => {
        setValue('grant_type', grantType);
    }

    const renderKeyValuePairs = (
        fields: Array<{ id: string }>,
        fieldNamePrefix: 'key_value_pairs' | 'custom_secrets',
        onAdd: () => void,
        onRemove: (index: number) => void
    ) => (
        <div className="flex flex-col gap-3">
            {fields.map((field, index) => (
                <div key={field.id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                    <Controller
                        name={`${fieldNamePrefix}.${index}.key` as const}
                        control={control}
                        render={({ field: keyField }) => (
                            <Input
                                {...keyField}
                                size="sm"
                                placeholder={t('Key')}
                                aria-label={t('Key')}
                                value={keyField.value ? String(keyField.value) : ''}
                            />
                        )}
                    />
                    <Controller
                        name={`${fieldNamePrefix}.${index}.value` as const}
                        control={control}
                        render={({ field: valueField }) => (
                            <Input
                                {...valueField}
                                size="sm"
                                placeholder={t('Value')}
                                aria-label={t('Value')}
                                value={valueField.value ? String(valueField.value) : ''}
                            />
                        )}
                    />
                    <div className="flex gap-2 justify-end">
                        <CtyButton
                            variant="light"
                            isIconOnly
                            color="primary"
                            size="sm"
                            onPress={onAdd}
                            title={t('Add')}
                        >
                            <Icon icon="mdi:plus" width="16" height="16" />
                        </CtyButton>
                        <CtyButton
                            variant="light"
                            isIconOnly
                            color="danger"
                            size="sm"
                            onPress={() => onRemove(index)}
                            title={t('Remove')}
                        >
                            <Icon icon="mdi:trash-can-outline" width="16" height="16" />
                        </CtyButton>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderField = (fieldConfig: FormFieldConfig | FormSelectConfig | FormTextareaConfig) => {
        if (fieldConfig.name === 'prefix' && headerName === 'X-API-Key') {
            return null;
        }
        if ('options' in fieldConfig) {
            const selectConfig = fieldConfig as FormSelectConfig;
            return (
                <FormSelect
                    key={selectConfig.name}
                    name={selectConfig.name}
                    control={control}
                    errors={errors}
                    label={selectConfig.label}
                    placeholder={selectConfig.placeholder}
                    required={selectConfig.required}
                    options={selectConfig.options}
                    defaultValue={selectConfig.defaultValue}
                    colSpan={selectConfig.colSpan}
                />
            );
        } else if ('minRows' in fieldConfig) {
            const textareaConfig = fieldConfig as FormTextareaConfig;
            return (
                <FormTextarea
                    key={textareaConfig.name}
                    name={textareaConfig.name}
                    control={control}
                    errors={errors}
                    label={textareaConfig.label}
                    placeholder={textareaConfig.placeholder}
                    required={textareaConfig.required}
                    minRows={textareaConfig.minRows}
                    maxRows={textareaConfig.maxRows}
                    colSpan={textareaConfig.colSpan}
                    validateJson={textareaConfig.validateJson}
                />
            );
        } else {
            const inputConfig = fieldConfig as FormFieldConfig;
            return (
                <FormField
                    key={inputConfig.name}
                    name={inputConfig.name}
                    control={control}
                    errors={errors}
                    label={inputConfig.label}
                    placeholder={inputConfig.placeholder}
                    required={inputConfig.required}
                    type={inputConfig.type}
                    colSpan={inputConfig.colSpan}
                    optional={inputConfig.optional}
                />
            );
        }
    };

    return (
        <div className="flex flex-1 flex-col gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CopyCodeField
                    label="Code"
                    value={code ?? ''}
                    className="md:col-span-2"
                    isReadOnly={!isCreateMode}
                    isRequired={false}
                    onValueChange={isCreateMode ? onCodeChange : undefined}
                />
                <Controller
                    name="name"
                    control={control}
                    rules={{
                        required: t('Credential Name is required')
                    }}
                    render={({ field }) => (
                        <div className="md:col-span-2">
                            <label className="flex items-center text-sm pb-1 font-medium">
                                {t('Credential Name')}<span className="text-danger">*</span>
                            </label>
                            <Input 
                                {...field} 
                                size="sm" 
                                placeholder={t('Enter credential name')}
                            />
                            {errors.name && <p className="text-danger text-xs mt-1">{errors.name.message}</p>}
                        </div>
                    )}
                />
                <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                        <div className="md:col-span-2">
                            <label className="flex items-center text-sm pb-1 font-medium">
                                {t('Description')}
                            </label>
                            <Textarea 
                                {...field}
                                size="sm"
                                minRows={3}
                                maxRows={6}
                                placeholder={t('Enter description')}
                                classNames={{
                                    input: "resize-y"
                                }}
                            />
                            {errors.description && <p className="text-danger text-xs mt-1">{errors.description.message}</p>}
                        </div>
                    )}
                />
                <Controller
                    name="credential_type"
                    control={control}
                    rules={{
                        required: t('Credential Type is required')
                    }}
                    render={({ field }) => (
                        <div className="md:col-span-2">
                            <label className="flex items-center text-sm pb-1 font-medium">
                                {t('Credential Type')}<span className="text-danger">*</span>
                            </label>
                            <Select 
                                id="credential-type"
                                aria-label="credential-type"
                                disallowEmptySelection
                                selectedKeys={field.value ? new Set([field.value]) : new Set()}
                                size="sm" 
                                placeholder={t('Select credential type')}
                                onSelectionChange={(keys) => {
                                    const selectedValue = Array.from(keys)[0] as string;
                                    if (selectedValue === 'oauth2') setGrantType('client_credentials');
                                    if (selectedValue === 'custom') {
                                        replaceCustomSecret([]);
                                        appendCustomSecret({ key: '', value: '' });
                                    }
                                    if (selectedValue === 'key_value') {
                                        setValue('value_type', 'string');
                                    }
                                    field.onChange(selectedValue);
                                }}
                            >
                                {credentialTypeOptions.map((type) => (
                                    <SelectItem key={type.key} value={type.key}>
                                        {t(type.value)}
                                    </SelectItem>
                                ))}
                            </Select>
                            {errors.credential_type && <p className="text-danger text-xs mt-1">{errors.credential_type.message}</p>}
                        </div>
                    )}
                />
                {credentialType.startsWith('oauth2') && (
                    <Controller
                        name="grant_type"
                        control={control}
                        rules={{
                            required: t('Grant Type is required')
                        }}
                        render={({ field }) => (
                            <div className="md:col-span-2">
                                <label className="flex items-center text-sm pb-1 font-medium">
                                    {t('Grant Type')}<span className="text-danger">*</span>
                                </label>
                                <Select
                                    id="grant-type"
                                    aria-label="grant-type"
                                    disallowEmptySelection
                                    selectedKeys={field.value ? new Set([field.value]) : new Set()}
                                    size="sm"
                                    placeholder={t('Select grant type')}
                                    onSelectionChange={(keys) => {
                                        const selectedValue = Array.from(keys)[0] as string;
                                        field.onChange(selectedValue);
                                    }}
                                >
                                    {grantTypeOptions.map((type) => (
                                        <SelectItem key={type.key} value={type.key}>
                                            {t(type.value)}
                                        </SelectItem>
                                    ))}
                                </Select>
                                {errors.grant_type && <p className="text-danger text-xs mt-1">{errors.grant_type.message}</p>}
                            </div>
                        )}
                    />
                )}
            </div>

            {/* Render Configuration Section */}
            {credentialConfigs[displayCredentialType]?.config && (
                <div className="mt-4">
                    <FormSection title={credentialConfigs[displayCredentialType].config!.title} icon={credentialConfigs[displayCredentialType].config!.icon} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {credentialConfigs[displayCredentialType].config!.fields.map(renderField)}
                    </div>
                </div>
            )}

            {/* Render Secrets Section */}
            {credentialConfigs[displayCredentialType]?.secrets && (
                <div className="mt-4">
                    <FormSection title={credentialConfigs[displayCredentialType].secrets!.title} icon={credentialConfigs[displayCredentialType].secrets!.icon} />
                    {credentialType === 'key_value' ? (
                        <>
                            {valueType === 'string' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <FormField
                                        name="value"
                                        control={control}
                                        errors={errors}
                                        label={t('Value')}
                                        placeholder={t('Enter value')}
                                        required={true}
                                        colSpan="full"
                                    />
                                </div>
                            )}
                            {valueType === 'key_value' && (
                                renderKeyValuePairs(
                                    keyValueFields,
                                    'key_value_pairs',
                                    () => appendKeyValue({ key: '', value: '' }),
                                    removeKeyValue
                                )
                            )}
                            {valueType === 'object' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <FormTextarea
                                        name="value_object"
                                        control={control}
                                        errors={errors}
                                        label={t('Value')}
                                        placeholder={t('Enter JSON object')}
                                        required={true}
                                        minRows={4}
                                        maxRows={8}
                                        colSpan="full"
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {credentialConfigs[displayCredentialType].secrets!.fields.map(renderField)}
                        </div>
                    )}
                </div>
            )}

            {/* Custom secret key-value pairs */}
            {credentialType === 'custom' && (
                <div className="mt-4">
                    <FormSection title={t('Secrets')} icon="hugeicons:lock" />
                    {renderKeyValuePairs(
                        customSecretFields,
                        'custom_secrets',
                        () => appendCustomSecret({ key: '', value: '' }),
                        removeCustomSecret
                    )}
                </div>
            )}
        </div>
    );
};

export default CredentialForm;
